import { type MouseEvent, useMemo, useState } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  AirRestrictionStatus,
  type DullesAmenityCanExist,
  type DullesUta,
  type FareDetails,
} from "@hopper-b2b/types";
import { camelCaseToTitleCase, capitalize } from "@hopper-b2b/utilities";
import { Box, Popover, Typography } from "@material-ui/core";
import type { TFunction } from "i18next";
import { IconName } from "../../Icon";
import { IconComponent } from "../../IconComponent";
import Amenity from "./Amenity";
import type { IFareNotice } from "./component";
import type { IRestrictionProps } from "./Restriction";

/**
 * Defines the grouping categories for fare amenities.
 * Amenities are organized into these groups for better UX and clarity.
 *
 * @constant
 * @readonly
 */
export const AMENITY_GROUPS = {
  /** Baggage-related amenities (carry-on, checked bags, personal items) */
  BAGGAGE: "included-baggage",
  /** Flexibility amenities (cancellation, changes, seat selection) */
  FLEXIBILITY: "flexibility",
  /** Additional perks and services (lounge access, priority boarding, etc.) */
  OTHER_INCLUSIONS: "other-inclusions",
  /** Aircraft-specific information (aircraft type, layout, seats) */
  AIRCRAFT: "aircraft",
} as const;

/**
 * Maps individual amenity category identifiers to their corresponding amenity group.
 * This mapping determines how amenities are organized and displayed in the UI.
 *
 * Categories not in this map will default to OTHER_INCLUSIONS group.
 *
 * @constant
 * @type {Record<string, string>}
 */
export const CATEGORY_TO_GROUP: Record<string, string> = {
  // Baggage
  "carry-on-allowance": AMENITY_GROUPS.BAGGAGE,
  "checked-bag-allowance": AMENITY_GROUPS.BAGGAGE,
  "personal-item": AMENITY_GROUPS.BAGGAGE,

  // Flexibility
  "advance-change": AMENITY_GROUPS.FLEXIBILITY,
  cancellation: AMENITY_GROUPS.FLEXIBILITY,
  "same-day-change": AMENITY_GROUPS.FLEXIBILITY,
  "seat-selection": AMENITY_GROUPS.FLEXIBILITY,

  // Other inclusions
  "lounge-access": AMENITY_GROUPS.OTHER_INCLUSIONS,
  "check-in-priority": AMENITY_GROUPS.OTHER_INCLUSIONS,
  "boarding-priority": AMENITY_GROUPS.OTHER_INCLUSIONS,
  entertainment: AMENITY_GROUPS.OTHER_INCLUSIONS,
  beverage: AMENITY_GROUPS.OTHER_INCLUSIONS,
  wifi: AMENITY_GROUPS.OTHER_INCLUSIONS,
  freshFood: AMENITY_GROUPS.OTHER_INCLUSIONS,

  // Aircraft
  aircraft: AMENITY_GROUPS.AIRCRAFT,
  layout: AMENITY_GROUPS.AIRCRAFT,
  seat: AMENITY_GROUPS.AIRCRAFT,
};

interface ISelectedFareRestrictionsProps {
  selectedFare: FareDetails;
  allFareDetails: FareDetails[];
  fareNotice?: IFareNotice[];
  index: number;
  isOutgoing: boolean;
  isMobile?: boolean;
  getEmptyRestrictionsText: (
    fareRating: number | undefined,
    translate: TFunction
  ) => string;
}
export const isDescriptionShort = (description: string, isMobile: boolean) => {
  if (isMobile) {
    // note: assuming each character is roughly 6px; it's considered short if it's less than 170px.
    return description.length * 6 < 170;
  } else {
    // note: assuming each character is roughly 6px; it's considered short if it's less than 270px.
    return description.length * 6 < 270;
  }
};

const SelectedFareRestrictions = ({
  selectedFare,
  allFareDetails,
  fareNotice,
  index,
  isOutgoing,
  isMobile,
  getEmptyRestrictionsText,
}: ISelectedFareRestrictionsProps) => {
  const { t } = useI18nContext();

  const GROUP_TITLES: Record<string, string> = {
    [AMENITY_GROUPS.BAGGAGE]: t("fareAmenities.baggage"),
    [AMENITY_GROUPS.FLEXIBILITY]: t("fareAmenities.flexibility"),
    [AMENITY_GROUPS.OTHER_INCLUSIONS]: t("fareAmenities.other"),
    [AMENITY_GROUPS.AIRCRAFT]: t("fareAmenities.aircraft"),
  };

  const [showAllAmenities, setShowAllAmenities] = useState<boolean>(false);
  const [hackerFareAnchorEl, setHackerFareAnchorEl] =
    useState<HTMLElement | null>(null);
  const fareIndex = selectedFare.slices.findIndex((slice) =>
    isOutgoing ? slice.outgoing : !slice.outgoing
  );
  const fareSlice = selectedFare.slices[fareIndex];

  const handleSeeMoreOnClick = () => setShowAllAmenities(!showAllAmenities);

  const restrictions = useMemo(() => {
    const result: IRestrictionProps[] = [];

    result.push(
      ...(fareSlice.amenitiesUtas?.utas.utas.map((uta: DullesUta) => {
        const restriction: IRestrictionProps = {
          // TODO: the assessment field should probably be an enum
          // Dulles for Uber is giving us incorrect info for several categories (hack)
          symbol:
            uta.category === "boarding-priority" &&
            ["fee", "restriction"].includes(uta.assessment)
              ? AirRestrictionStatus.GENERIC
              : uta.category === "carry-on-allowance" &&
                  uta.assessment === "restriction"
                ? AirRestrictionStatus.GENERIC
                : uta.category === "cancellation" &&
                    uta.headline === "Non-refundable" // !!! translated strings will not match this copy
                  ? AirRestrictionStatus.GENERIC
                  : uta.assessment === "benefit"
                    ? AirRestrictionStatus.INCLUDED
                    : uta.assessment === "fee"
                      ? AirRestrictionStatus.PURCHASABLE
                      : uta.assessment === "restriction"
                        ? AirRestrictionStatus.UNAVAILABLE
                        : AirRestrictionStatus.GENERIC,
          // TODO: probably need a different type for category
          description: `${uta.description}`,
          category: uta.category,
          group:
            CATEGORY_TO_GROUP[uta.category] || AMENITY_GROUPS.OTHER_INCLUSIONS,
        };

        return restriction;
      }) ?? [])
    );
    if (fareSlice.amenitiesUtas?.amenities) {
      result.push(
        ...Object.keys(fareSlice.amenitiesUtas.amenities).map((key) => {
          const amenitity: DullesAmenityCanExist = (
            fareSlice.amenitiesUtas?.amenities as any
          )?.[key];
          const symbol =
            amenitity.exists === "yes"
              ? AirRestrictionStatus.INCLUDED
              : amenitity.exists === "no"
                ? AirRestrictionStatus.UNAVAILABLE
                : AirRestrictionStatus.GENERIC;
          return {
            symbol: symbol,
            description: `${capitalize(camelCaseToTitleCase(key))} - ${
              amenitity.info.displayText
            }`,
            category: key,
            group: CATEGORY_TO_GROUP[key] || AMENITY_GROUPS.OTHER_INCLUSIONS,
          };
        })
      );
    }

    return result;
  }, [fareSlice.amenitiesUtas]);

  const groupedRestrictions = useMemo(() => {
    const groups: Record<string, IRestrictionProps[]> = {
      [AMENITY_GROUPS.BAGGAGE]: [],
      [AMENITY_GROUPS.FLEXIBILITY]: [],
      [AMENITY_GROUPS.OTHER_INCLUSIONS]: [],
      [AMENITY_GROUPS.AIRCRAFT]: [],
    };

    restrictions.forEach((r) => {
      const group = r.group || AMENITY_GROUPS.OTHER_INCLUSIONS;
      groups[group].push(r);
    });

    return groups;
  }, [restrictions]);

  const showFareNotice = fareNotice?.find((n) => n?.id === selectedFare.id);
  const handleHackerFarePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setHackerFareAnchorEl(event.currentTarget);
  };
  const handleHackerFarePopoverClose = () => {
    setHackerFareAnchorEl(null);
  };

  if (restrictions) {
    return (
      <Box className="amenities flight-card-mini">
        {fareSlice.fareShelf?.brandName && (
          <Typography variant="h6" className="selected-fare-name">
            {fareSlice.fareShelf.brandName}
          </Typography>
        )}

        {restrictions.length === 0 && (
          <Typography
            variant="body1"
            className="trip-fare-empty-restriction-text"
          >
            {getEmptyRestrictionsText(fareSlice.fareShelf?.rating, t)}
          </Typography>
        )}

        {Object.entries(groupedRestrictions).map(([groupKey, items]) => {
          const isAlwaysVisible =
            groupKey === AMENITY_GROUPS.BAGGAGE ||
            groupKey === AMENITY_GROUPS.FLEXIBILITY;
          const shouldShow = isAlwaysVisible || showAllAmenities;

          return items.length > 0 && shouldShow ? (
            <Box key={groupKey} className="amenity-group">
              <Typography variant="subtitle2" className="group-header">
                {GROUP_TITLES[groupKey]}
              </Typography>
              {items.map((r, i) => (
                <Amenity key={`${groupKey}-Amenity-${i}`} restriction={r} />
              ))}
            </Box>
          ) : null;
        })}
        {!showAllAmenities &&
          (groupedRestrictions[AMENITY_GROUPS.OTHER_INCLUSIONS].length > 0 ||
            groupedRestrictions[AMENITY_GROUPS.AIRCRAFT].length > 0) && (
            <Typography
              className="show-more"
              variant="h6"
              role="button"
              onClick={handleSeeMoreOnClick}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSeeMoreOnClick();
                }
              }}
              tabIndex={0}
            >
              {t("viewAllAmenities", {
                count: restrictions.length,
              })}
            </Typography>
          )}
        {!!showFareNotice && !isOutgoing ? (
          <>
            <Typography
              className="fare-notice"
              variant="body2"
              onClick={(event) => handleHackerFarePopoverOpen(event)}
            >
              {showFareNotice.message}
              <IconComponent
                ariaLabel="Fare notice info icon"
                className="fare-notice-info-icon"
                name={IconName.InfoCircle}
              />
            </Typography>
            <Popover
              id="mouse-over-popover"
              className="hacker-fare-popover"
              open={!!hackerFareAnchorEl}
              anchorEl={hackerFareAnchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              onClose={handleHackerFarePopoverClose}
            >
              <Typography
                className="farenotice-info"
                variant="body2"
                onMouseLeave={handleHackerFarePopoverClose}
              >
                {showFareNotice.tooltipCopy}
              </Typography>
            </Popover>
          </>
        ) : null}
      </Box>
    );
  }
  return null;
};

export default SelectedFareRestrictions;
